import * as classNames from 'classnames';
import { compact } from 'lodash';
import { contentType } from 'mime-types';
import * as React from 'react';
import { useCustomEventEffect } from '../../hooks';
import { sanitizeContent } from '../../thumbtack';
import { directUpload, ErrorWithAdditionalData } from '../../utilities/directUpload';
import { reportError } from '../../utilities/reportError';
import { FileDisplay, IFile } from '../fileUpload/FileDisplay';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface IDocument {
  accept: string[];
  name: string;
  documentDefaultValue?: IFile;
  translations: { documentLabelText: string };
  questionDetails: IQuestionDetails;
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export function Document(props: IDocument) {
  const {
    accept,
    name,
    documentDefaultValue,
    translations,
    questionDetails,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;
  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [labelText, setLabelText] = React.useState<string>(translations.documentLabelText || 'Choose a File');
  const [isSelected, setIsSelected] = React.useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = React.useState<IFile>(documentDefaultValue);
  const [error, setError] = React.useState<string>();
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({ questionType: 'document', value: !!uploadedFile });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };
      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  function handleInputChange(event: React.SyntheticEvent) {
    setError(null);
    const input = event.target as HTMLInputElement;
    const filesCount = input.files.length;

    setIsSelected(filesCount > 0);

    let fileName = labelText;
    if (filesCount > 0) {
      fileName = input.files[0].name;
      upload(input.files[0]);
      announceAnswerUpdate({ questionType: 'document', value: true });
    }
    setLabelText(fileName);
  }

  function upload(file: File) {
    setLabelText(`Uploading ${file.name}`);

    directUpload(file, onProgress)
      .then((blob) => {
        setUploadedFile({ name: file.name, signedId: blob.signed_id });
      })
      .catch((e: Error | ErrorWithAdditionalData) => {
        let status: number;
        if ('additionalData' in e && e.additionalData) {
          status = e.additionalData.status;
        }

        // E.g. Error creating Blob for file. Status: 422
        if (status === 422) {
          setError('Upload failed due to session timeout. Please refresh the page and try again.');
        } else {
          setError('Upload failed. Please try again.');
        }

        if ('error' in e) {
          reportError(e.error, e.additionalData);
        } else {
          reportError(e);
        }

        resetFileInput();
      });
  }

  function onProgress(progress: number) {
    setUploadProgress(progress);
  }

  function resetFileInput() {
    setLabelText('Choose a File');
    setUploadProgress(0);
    setIsSelected(false);
    announceAnswerUpdate({ questionType: 'document', value: false });
  }

  function handleRemove(file: IFile) {
    setUploadedFile(null);
    resetFileInput();
    announceAnswerUpdate({ questionType: 'document', value: false });
  }

  const acceptedFileTypes = compact(accept.map((fileType) => contentType(fileType))).join(',');

  const fileFieldClassName = 'file-field file-field--themed';

  const labelClassName = classNames('file-field__label', { 'file-field__label--selected': isSelected });

  const fileInputField = (
    <div className={fileFieldClassName}>
      <input
        accept={acceptedFileTypes}
        className="file-field__input"
        id={name}
        onChange={handleInputChange}
        multiple={false}
        name={name}
        type="file"
        required={required}
      />
      <label htmlFor={name} className={labelClassName}>
        <i className="fal fa-paperclip mar-r-1" />
        {translations.documentLabelText}
        <div className="file-field__progress" style={{ width: `${uploadProgress}%` }} />
      </label>
      {error && <div className="file-field__error">{error}</div>}
    </div>
  );

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_document_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span dangerouslySetInnerHTML={{ __html: sanitizeContent(additionalInformation) }} />}
      </label>
      {uploadedFile ? <FileDisplay file={uploadedFile} handleRemove={handleRemove} name={name} /> : fileInputField}
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}

// used directly, so need default import.
export default Document;
